import { useState, useRef, useEffect } from 'react'
import { useChatStore } from '../../store/chatStore'
import { 
  XMarkIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline'

const CreateChatModal = ({ isOpen, onClose }) => {
  const [chatType, setChatType] = useState('direct') // 'direct' or 'group'
  const [chatName, setChatName] = useState('')
  const [chatDescription, setChatDescription] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [chatAvatar, setChatAvatar] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const debounceRef = useRef(null)

  const { searchUsers, createChat } = useChatStore()

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearch = async (query) => {
    console.log('🔍 Search called with query:', query)
    if (!query.trim() || query.length < 2) {
      console.log('❌ Query too short or empty')
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      console.log('📡 Calling searchUsers API...')
      const results = await searchUsers(query)
      console.log('✅ Search results received:', results)
      setSearchResults(results)
    } catch (error) {
      console.error('❌ Search error:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearchChange = (e) => {
    const query = e.target.value
    console.log('🔤 Input changed to:', query)
    setSearchQuery(query)
    
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    
    // Set new timeout
    debounceRef.current = setTimeout(() => {
      console.log('⏰ Debounce timeout triggered for:', query)
      handleSearch(query)
    }, 300)
  }

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === user._id)
      if (isSelected) {
        return prev.filter(u => u._id !== user._id)
      } else {
        return [...prev, user]
      }
    })
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setChatAvatar(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (selectedUsers.length === 0) return
    
    if (chatType === 'group' && !chatName.trim()) {
      alert('Please enter a group name')
      return
    }

    setIsLoading(true)
    try {
      const chatData = {
        participants: selectedUsers.map(u => u._id),
        isGroupChat: chatType === 'group',
        name: chatType === 'group' ? chatName : undefined,
        description: chatType === 'group' ? chatDescription : undefined,
        avatar: chatAvatar
      }

      await createChat(chatData.participants, chatData.isGroupChat, chatData.name, chatData.description)
      
      // Reset form
      setChatType('direct')
      setChatName('')
      setChatDescription('')
      setSelectedUsers([])
      setSearchQuery('')
      setSearchResults([])
      setChatAvatar(null)
      setAvatarPreview(null)
      
      onClose()
    } catch (error) {
      console.error('Create chat error:', error)
      alert('Failed to create chat. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setChatType('direct')
    setChatName('')
    setChatDescription('')
    setSelectedUsers([])
    setSearchQuery('')
    setSearchResults([])
    setChatAvatar(null)
    setAvatarPreview(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Create New Chat
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          {/* Chat Type Selection */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setChatType('direct')}
                className={`
                  p-4 rounded-lg border-2 transition-colors text-left
                  ${chatType === 'direct' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }
                `}
              >
                <ChatBubbleLeftRightIcon className={`h-8 w-8 mb-2 ${
                  chatType === 'direct' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'
                }`} />
                <h3 className="font-medium text-gray-900 dark:text-white">Direct Message</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  One-on-one conversation
                </p>
              </button>

              <button
                type="button"
                onClick={() => setChatType('group')}
                className={`
                  p-4 rounded-lg border-2 transition-colors text-left
                  ${chatType === 'group' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }
                `}
              >
                <UserGroupIcon className={`h-8 w-8 mb-2 ${
                  chatType === 'group' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'
                }`} />
                <h3 className="font-medium text-gray-900 dark:text-white">Group Chat</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Chat with multiple people
                </p>
              </button>
            </div>
          </div>

          {/* Group Chat Details */}
          {chatType === 'group' && (
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
              {/* Group Avatar */}
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Group avatar"
                      className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors">
                    <PhotoIcon className="h-4 w-4 mr-2" />
                    Choose Avatar
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleAvatarChange}
                    />
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional group avatar
                  </p>
                </div>
              </div>

              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={chatName}
                  onChange={(e) => setChatName(e.target.value)}
                  placeholder="Enter group name..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  required
                />
              </div>

              {/* Group Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={chatDescription}
                  onChange={(e) => setChatDescription(e.target.value)}
                  placeholder="What's this group about? (optional)"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                />
              </div>
            </div>
          )}

          {/* User Search */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Add Participants {chatType === 'direct' ? '(1 user)' : '(2+ users)'}
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search users by username or display name..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                </div>
              )}
            </div>

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Selected ({selectedUsers.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map(user => (
                    <div
                      key={user._id}
                      className="inline-flex items-center bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-3 py-1 rounded-full text-sm"
                    >
                      <span>{user.username}</span>
                      <button
                        type="button"
                        onClick={() => toggleUserSelection(user)}
                        className="ml-2 hover:text-primary-600 dark:hover:text-primary-300"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto p-6">
            {searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map(user => {
                  const isSelected = selectedUsers.some(u => u._id === user._id)
                  const isDisabled = chatType === 'direct' && selectedUsers.length >= 1 && !isSelected
                  
                  return (
                    <div
                      key={user._id}
                      onClick={() => !isDisabled && toggleUserSelection(user)}
                      className={`
                        flex items-center p-3 rounded-lg transition-colors
                        ${isDisabled 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'cursor-pointer'
                        }
                        ${isSelected 
                          ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      {/* Avatar */}
                      <div className="flex-shrink-0 mr-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {(user.username).charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.username}
                        </p>
                      </div>

                      {/* Selection Indicator */}
                      <div className="flex-shrink-0 ml-3">
                        {isSelected ? (
                          <div className="w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : (
                          <UserPlusIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : searchQuery.length >= 2 && !isSearching ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No users found for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <UserPlusIcon className="h-12 w-12 mx-auto mb-2" />
                <p>Search for users to add to your chat</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={
                  isLoading || 
                  selectedUsers.length === 0 || 
                  (chatType === 'direct' && selectedUsers.length !== 1) ||
                  (chatType === 'group' && (selectedUsers.length < 2 || !chatName.trim()))
                }
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  `Create ${chatType === 'direct' ? 'Direct Message' : 'Group Chat'}`
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateChatModal